interface FooterSectionProps {
  onInspectionClick: () => void;
}

const socialLinks = [
  { href: "https://www.facebook.com/clwizard", icon: "ri-facebook-fill", label: "Facebook" },
  { href: "https://www.instagram.com/clwizard/", icon: "ri-instagram-fill", label: "Instagram" },
  { href: "https://www.youtube.com/@CLwizard", icon: "ri-youtube-fill", label: "YouTube" },
  { href: "https://www.linkedin.com/in/marcofilhoengineer/", icon: "ri-linkedin-fill", label: "LinkedIn" },
];

const services = [
  "Concrete Leak Injection",
  "Hydrostatic Pressure Treatment",
  "Parking Garage Waterproofing",
  "Facade Infiltration Repair",
  "Condominium Lobby Treatment",
];

export default function FooterSection({ onInspectionClick }: FooterSectionProps) {
  return (
    <footer className="bg-[#060810]">
      {/* CTA band */}
      <div className="bg-[#0a0e1a] border-t border-[#1a3a5c]/30 py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
            Stop the Cycle. Treat the Source.
          </h2>
          <p className="text-gray-400 text-base mb-8 max-w-xl mx-auto">
            One free engineering inspection. No obligation. No guesswork. Just a clear technical answer to why your building keeps leaking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onInspectionClick}
              className="bg-[#1a3a5c] hover:bg-[#0f2a45] text-white font-extrabold px-8 py-4 rounded-md transition-colors whitespace-nowrap text-base"
            >
              Schedule a Free Inspection
            </button>
            <a
              href="tel:5617745120"
              className="border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-md hover:bg-white/5 transition-colors whitespace-nowrap text-base flex items-center justify-center gap-2"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill"></i>
              </span>
              (561) 774-5120
            </a>
          </div>
        </div>
      </div>

      {/* Footer body */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">

          {/* Brand */}
          <div>
            <img
              src="https://clwizard.com/wp-content/uploads/2022/05/Blushot-Logo-2-®.png"
              alt="CLWizard Logo"
              className="h-12 w-auto object-contain mb-4"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "https://static.readdy.ai/image/61892072331720d5ae52870a50f3ccd8/7ae8d477771f73e998ed5d5ba3a2bcfc.png";
              }}
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              We don&apos;t patch leaks. We engineer permanent solutions.
            </p>
            {/* Social links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-gray-400 hover:text-white hover:border-[#4a7fa5] transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Services</h4>
            <ul className="flex flex-col gap-2.5">
              {services.map((s) => (
                <li key={s}>
                  <a href="#difference" className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-2">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-[#4a7fa5] text-xs"></i>
                    </span>
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Contact</h4>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-[#1a3a5c]/20">
                  <i className="ri-phone-fill text-[#4a7fa5] text-sm"></i>
                </span>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Phone</p>
                  <a href="tel:5617745120" className="text-gray-300 text-sm hover:text-white transition-colors font-semibold">
                    (561) 774-5120
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-[#1a3a5c]/20">
                  <i className="ri-mail-fill text-[#4a7fa5] text-sm"></i>
                </span>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Email</p>
                  <a href="mailto:info@clwizard.com" className="text-gray-300 text-sm hover:text-white transition-colors font-semibold">
                    info@clwizard.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-[#1a3a5c]/20">
                  <i className="ri-map-pin-fill text-[#4a7fa5] text-sm"></i>
                </span>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Service Area</p>
                  <span className="text-gray-300 text-sm font-semibold">South Florida &amp; Nationwide</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-[#1a3a5c]/20">
                  <i className="ri-shield-star-fill text-[#4a7fa5] text-sm"></i>
                </span>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Guarantee</p>
                  <span className="text-gray-300 text-sm font-semibold">5-Year Written Warranty</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">
            &copy; 2024 CLWizard® — The Concrete Leak Specialist. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 text-xs">Licensed &amp; Insured</span>
            <span className="text-gray-700">|</span>
            <span className="text-gray-600 text-xs">5-Year Warranty</span>
            <span className="text-gray-700">|</span>
            <span className="text-gray-600 text-xs">Engineer-Approved</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
